import { useState, type FormEvent, type ReactNode } from 'react';
import type {
  AiEndpointHealth,
  AiEndpoint,
  AiEndpointBody,
  AiEndpointTest,
  AiModelList,
  AiModelsRequest,
  AiOverview,
  AiService,
  AiThinking,
  AiUsageDay,
  TenantAi,
  TenantAiView,
} from 'zitimail-shared-types';

import { api, describeError } from './api';
import { ActionButton, Badge, Loaded, useLoad, when } from './ui';

// ─── AI models (platform) ────────────────────────────────────────────────────

/**
 * Whether the model answers, from the platform's check each minute
 * (operators are emailed when it stops). Nothing before the first check.
 */
function Answering({ health }: { health: AiEndpointHealth | undefined }) {
  if (!health?.checked_at) {
    return null;
  }
  if (health.down_since) {
    return (
      <div>
        <Badge tone="crit">Not answering</Badge>
        <div className="adm-muted adm-small">
          since {when(health.down_since)}
          {health.last_error ? `: ${health.last_error}` : ''}
        </div>
      </div>
    );
  }
  return (
    <div>
      <Badge tone={health.failures > 0 ? 'warn' : 'ok'}>
        {health.failures > 0 ? 'Missed a check' : 'Answering'}
      </Badge>
    </div>
  );
}

export function AiPage() {
  const [load, reload] = useLoad((signal) => api<AiOverview>('/platform/ai', { signal }), 'ai');
  return (
    <>
      <div className="adm-head">
        <h2>AI models</h2>
      </div>
      <p className="adm-muted">
        Two features use a model: the spam check, on mail the spam filter cannot decide on, and the
        writing assistant in webmail. Any server that speaks the OpenAI API works — Ollama, vLLM, or
        a cloud provider. Tenants choose whether they use each feature on their own page.
      </p>
      <Loaded load={load}>
        {(overview) => (
          <>
            <Models overview={overview} onChanged={reload} />
            <section className="adm-panel">
              <h3>Which model each feature uses</h3>
              <p className="adm-muted adm-small">
                Models are tried in this order; the next one is used only when the one before it
                does not answer. Cloud models are only used for tenants allowed to send content to
                the cloud — for every other tenant they are skipped.
              </p>
              <div className="adm-ai-services">
                <ServiceOrder
                  service="sentry"
                  title="Spam check (AI Sentry)"
                  overview={overview}
                  onChanged={reload}
                >
                  Judges whether uncertain mail is phishing, a scam or impersonation. If no model
                  answers, mail is delivered as normal. It reads every uncertain message, so a
                  stronger model is usually worth it here.
                </ServiceOrder>
                <ServiceOrder
                  service="compose"
                  title="Writing assistant"
                  overview={overview}
                  onChanged={reload}
                >
                  Drafts new messages and replies in webmail. If no model answers, people see
                  &ldquo;Currently unavailable&rdquo;. Someone is waiting for each draft, so a
                  smaller, faster model often suits it better.
                </ServiceOrder>
              </div>
            </section>
          </>
        )}
      </Loaded>
    </>
  );
}

function Where({ endpoint }: { endpoint: AiEndpoint }) {
  return endpoint.local ? <Badge tone="ok">Local</Badge> : <Badge tone="warn">Cloud</Badge>;
}

function Models({ overview, onChanged }: { overview: AiOverview; onChanged: () => void }) {
  const [editing, setEditing] = useState<string | null>(null);
  const [tests, setTests] = useState<Record<string, AiEndpointTest>>({});
  const edited = overview.endpoints.find((e) => e.id === editing);

  return (
    <section className="adm-panel">
      <h3>Models</h3>
      {overview.endpoints.length === 0 ? (
        <p className="adm-muted">No models yet. Add the first one below.</p>
      ) : (
        <table className="adm-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Address</th>
              <th>Where</th>
              <th>API key</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {overview.endpoints.map((e) => {
              const test = tests[e.id];
              return (
                <tr key={e.id}>
                  <td>
                    <strong>{e.name}</strong>
                    <div className="adm-mono adm-small adm-muted">{e.model}</div>
                  </td>
                  <td className="adm-mono adm-small">
                    {e.base_url}
                    {e.thinking !== 'auto' && (
                      <div className="adm-muted">
                        {e.thinking === 'off' ? 'Thinking off' : 'Thinking allowed'}
                      </div>
                    )}
                  </td>
                  <td>
                    <Where endpoint={e} />
                  </td>
                  <td>{e.has_key ? 'Set' : <span className="adm-muted">None</span>}</td>
                  <td>
                    <Badge tone={e.enabled ? 'ok' : 'neutral'}>
                      {e.enabled ? 'Enabled' : 'Disabled'}
                    </Badge>
                    <Answering health={e.enabled ? overview.health?.[e.id] : undefined} />
                  </td>
                  <td>
                    <span className="adm-actions adm-ai-acts">
                      <ActionButton
                        tone="ghost"
                        run={async () => {
                          const result = await api<AiEndpointTest>(
                            `/platform/ai/endpoints/${encodeURIComponent(e.id)}/test`,
                            { method: 'POST' },
                          );
                          setTests((all) => ({ ...all, [e.id]: result }));
                        }}
                      >
                        Test
                      </ActionButton>
                      <button
                        type="button"
                        className="btn ghost"
                        onClick={() => setEditing(editing === e.id ? null : e.id)}
                      >
                        Edit
                      </button>
                      <ActionButton
                        tone="ghost"
                        run={async () => {
                          await api(`/platform/ai/endpoints/${encodeURIComponent(e.id)}/delete`, {
                            method: 'POST',
                          });
                          if (editing === e.id) {
                            setEditing(null);
                          }
                          onChanged();
                        }}
                      >
                        Remove
                      </ActionButton>
                    </span>
                    {test && <TestResult test={test} />}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      <p className="adm-muted adm-small">
        Test sends a sample suspicious email and shows what the model answers. It can take as long
        as the model&apos;s timeout.
      </p>
      {edited ? (
        <EndpointForm
          key={edited.id}
          endpoint={edited}
          keyStorage={overview.key_storage}
          onDone={() => {
            setEditing(null);
            onChanged();
          }}
          onCancel={() => setEditing(null)}
          onChanged={onChanged}
        />
      ) : (
        <EndpointForm key="new" keyStorage={overview.key_storage} onDone={onChanged} />
      )}
    </section>
  );
}

function TestResult({ test }: { test: AiEndpointTest }) {
  if (!test.ok) {
    return (
      <div className="adm-ai-test" role="status">
        <Badge tone="crit">No answer</Badge> after {test.latency_ms} ms
        <div className="adm-error">{test.error}</div>
      </div>
    );
  }
  return (
    <div className="adm-ai-test" role="status">
      <Badge tone="ok">Answered</Badge> in {test.latency_ms} ms
      {test.verdict ? (
        <>
          {' '}
          · verdict <strong>{test.verdict}</strong>
        </>
      ) : (
        ' · no verdict in the reply'
      )}
      <pre className="adm-output">{test.reply}</pre>
    </div>
  );
}

type Placement = 'auto' | 'local' | 'cloud';

function EndpointForm({
  endpoint,
  keyStorage,
  onDone,
  onCancel,
  onChanged,
}: {
  endpoint?: AiEndpoint;
  keyStorage: boolean;
  onDone: () => void;
  onCancel?: () => void;
  /** After removing the key, which leaves the form open. */
  onChanged?: () => void;
}) {
  const [name, setName] = useState(endpoint?.name ?? '');
  const [baseUrl, setBaseUrl] = useState(endpoint?.base_url ?? '');
  const [model, setModel] = useState(endpoint?.model ?? '');
  const [placement, setPlacement] = useState<Placement>(
    endpoint ? (endpoint.local ? 'local' : 'cloud') : 'auto',
  );
  const [apiKey, setApiKey] = useState('');
  const [timeout, setTimeoutSecs] = useState(endpoint?.timeout_secs ?? 30);
  const [thinking, setThinking] = useState<AiThinking>(endpoint?.thinking ?? 'auto');
  const [enabled, setEnabled] = useState(endpoint?.enabled ?? true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** What the server said it serves, once asked; null before. */
  const [served, setServed] = useState<readonly string[] | null>(null);
  const [fetching, setFetching] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const fetchModels = () => {
    setFetching(true);
    setFetchError(null);
    const body: AiModelsRequest = {
      base_url: baseUrl.trim(),
      ...(keyStorage && apiKey ? { api_key: apiKey } : {}),
      ...(endpoint ? { endpoint_id: endpoint.id } : {}),
    };
    api<AiModelList>('/platform/ai/models', { method: 'POST', body })
      .then(({ models }) => {
        setServed(models);
        // One model, or none chosen yet: take the first, which is usually it.
        if (models.length > 0 && !models.includes(model.trim())) {
          setModel(models.length === 1 || !model.trim() ? models[0]! : model);
        }
      })
      .catch((e: unknown) => {
        setServed(null);
        setFetchError(describeError(e));
      })
      .finally(() => setFetching(false));
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    const body: AiEndpointBody = {
      name: name.trim(),
      base_url: baseUrl.trim(),
      model: model.trim(),
      enabled,
      timeout_secs: timeout,
      thinking,
      ...(placement === 'auto' ? {} : { local: placement === 'local' }),
      ...(keyStorage && apiKey ? { api_key: apiKey } : {}),
    };
    api<AiEndpoint>(
      endpoint
        ? `/platform/ai/endpoints/${encodeURIComponent(endpoint.id)}`
        : '/platform/ai/endpoints',
      { method: 'POST', body },
    )
      .then(() => {
        if (!endpoint) {
          setName('');
          setBaseUrl('');
          setModel('');
          setPlacement('auto');
          setTimeoutSecs(30);
          setEnabled(true);
        }
        setApiKey('');
        onDone();
      })
      .catch((e: unknown) => setError(describeError(e)))
      .finally(() => setBusy(false));
  };

  return (
    <form className="adm-form adm-sub" onSubmit={submit}>
      <h4>{endpoint ? `Edit ${endpoint.name}` : 'Add a model'}</h4>
      <div className="adm-fields">
        <label>
          Name
          <input value={name} onChange={(e) => setName(e.target.value)} required maxLength={100} />
        </label>
        <label>
          Address (OpenAI-compatible)
          <input
            value={baseUrl}
            onChange={(e) => setBaseUrl(e.target.value)}
            placeholder="http://gpu-01.internal:11434/v1"
            required
          />
        </label>
        <label>
          <span>
            API key <span className="adm-muted">(optional)</span>
          </span>
          <input
            type="password"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            disabled={!keyStorage}
            autoComplete="new-password"
            placeholder={
              !keyStorage
                ? 'Unavailable'
                : endpoint?.has_key
                  ? 'Leave blank to keep the current key'
                  : ''
            }
          />
        </label>
        <div className="adm-model-field">
          <label>
            Model
            {served && served.length > 0 ? (
              <select value={model} onChange={(e) => setModel(e.target.value)} required>
                {!served.includes(model) && model && <option value={model}>{model}</option>}
                {served.map((id) => (
                  <option key={id} value={id}>
                    {id}
                  </option>
                ))}
              </select>
            ) : (
              <input
                value={model}
                onChange={(e) => setModel(e.target.value)}
                placeholder="qwen2.5:7b-instruct"
                required
              />
            )}
          </label>
          <button
            type="button"
            className="btn ghost"
            onClick={fetchModels}
            disabled={fetching || !baseUrl.trim()}
          >
            {fetching ? 'Asking…' : served ? 'Fetch again' : 'Fetch models'}
          </button>
          {served && served.length === 0 && (
            <p className="adm-note">The server lists no models; type the name instead.</p>
          )}
          {served && served.length > 0 && (
            <p className="adm-note">
              {served.length === 1
                ? 'The server serves 1 model.'
                : `The server serves ${served.length} models.`}{' '}
              <button type="button" className="adm-link" onClick={() => setServed(null)}>
                Type a name instead
              </button>
            </p>
          )}
          {fetchError && (
            <p className="adm-note adm-error" role="alert">
              Could not list the models: {fetchError}
            </p>
          )}
        </div>
        <label>
          Where it runs
          <select value={placement} onChange={(e) => setPlacement(e.target.value as Placement)}>
            <option value="auto">Decide from the address</option>
            <option value="local">Local — on our own servers</option>
            <option value="cloud">Cloud — a provider outside Genworx</option>
          </select>
        </label>
        <label>
          Timeout, seconds
          <input
            type="number"
            min={1}
            max={300}
            value={timeout}
            onChange={(e) => setTimeoutSecs(Number(e.target.value))}
          />
        </label>
        <label>
          Thinking
          <select value={thinking} onChange={(e) => setThinking(e.target.value as AiThinking)}>
            <option value="auto">Automatic — turn it off if it gets in the way</option>
            <option value="off">Off — ask the model not to think</option>
            <option value="on">Allowed — give it room to think</option>
          </select>
        </label>
      </div>
      <p className="adm-muted">
        Reasoning models (Qwen 3, DeepSeek-R1) think before they answer, and can use up the whole
        answer doing it. Automatic asks such a model not to think as soon as that happens. Allowed
        is slower: raise the timeout too.
      </p>
      {!keyStorage && (
        <p className="adm-note">
          API keys can&apos;t be stored: the server has no encryption key set.
        </p>
      )}
      <p className="adm-muted adm-small">
        &ldquo;Decide from the address&rdquo; treats private and loopback addresses (10.x,
        192.168.x, *.internal, localhost) as local and anything else as cloud.
      </p>
      <div className="adm-inline">
        <label className="adm-check">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          Enabled
        </label>
        <button type="submit" className="btn" disabled={busy}>
          {busy ? 'Saving…' : endpoint ? 'Save changes' : 'Add model'}
        </button>
        {onCancel && (
          <button type="button" className="btn ghost" onClick={onCancel}>
            Cancel
          </button>
        )}
        {endpoint?.has_key && keyStorage && (
          <ActionButton
            tone="ghost"
            run={async () => {
              await api(`/platform/ai/endpoints/${encodeURIComponent(endpoint.id)}`, {
                method: 'POST',
                body: {
                  name: endpoint.name,
                  base_url: endpoint.base_url,
                  model: endpoint.model,
                  local: endpoint.local,
                  enabled: endpoint.enabled,
                  timeout_secs: endpoint.timeout_secs,
                  thinking: endpoint.thinking,
                  api_key: '',
                } satisfies AiEndpointBody,
              });
              onChanged?.();
            }}
          >
            Remove key
          </ActionButton>
        )}
      </div>
      {error && (
        <p className="adm-error" role="alert">
          {error}
        </p>
      )}
    </form>
  );
}

function ServiceOrder({
  service,
  title,
  overview,
  onChanged,
  children,
}: {
  service: AiService;
  title: string;
  overview: AiOverview;
  onChanged: () => void;
  children: ReactNode;
}) {
  const byId = new Map(overview.endpoints.map((e) => [e.id, e]));
  // An id whose endpoint is gone is dropped from view; the next save drops it
  // from the list too.
  const order = (overview.services[service] ?? []).filter((id) => byId.has(id));
  const unassigned = overview.endpoints.filter((e) => !order.includes(e.id));
  const [pick, setPick] = useState('');
  const [error, setError] = useState<string | null>(null);

  const save = (ids: string[]) => {
    setError(null);
    api(`/platform/ai/services/${service}`, { method: 'POST', body: { endpoint_ids: ids } })
      .then(onChanged)
      .catch((e: unknown) => setError(describeError(e)));
  };
  const move = (from: number, to: number) => {
    const next = [...order];
    const [id] = next.splice(from, 1);
    if (id !== undefined) {
      next.splice(to, 0, id);
      save(next);
    }
  };

  return (
    <div className="adm-ai-service">
      <h4>{title}</h4>
      <p className="adm-muted adm-small">{children}</p>
      {order.length === 0 ? (
        <p className="adm-muted">
          No model assigned.{' '}
          {service === 'sentry'
            ? 'Mail is delivered without this check.'
            : 'The assistant shows “Currently unavailable”.'}
        </p>
      ) : (
        <ol className="adm-list adm-ai-order" aria-label={`${title}: models in order`}>
          {order.map((id, i) => {
            const e = byId.get(id);
            if (!e) {
              return null;
            }
            return (
              <li key={id}>
                <span className="adm-ai-rank">{i + 1}</span>
                <span>
                  <strong>{e.name}</strong>{' '}
                  <span className="adm-mono adm-small adm-muted">{e.model}</span>
                </span>
                <Where endpoint={e} />
                {!e.enabled && <Badge tone="neutral">Disabled</Badge>}
                <span className="adm-actions adm-ai-moves">
                  <button
                    type="button"
                    className="btn ghost"
                    disabled={i === 0}
                    onClick={() => move(i, i - 1)}
                    aria-label={`Move ${e.name} up`}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className="btn ghost"
                    disabled={i === order.length - 1}
                    onClick={() => move(i, i + 1)}
                    aria-label={`Move ${e.name} down`}
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    className="btn ghost"
                    onClick={() => save(order.filter((x) => x !== id))}
                    aria-label={`Remove ${e.name} from ${title}`}
                  >
                    Remove
                  </button>
                </span>
              </li>
            );
          })}
        </ol>
      )}
      {unassigned.length > 0 && (
        <div className="adm-inline">
          <select
            value={pick}
            onChange={(e) => setPick(e.target.value)}
            aria-label={`Model to add to ${title}`}
          >
            <option value="">Choose a model…</option>
            {unassigned.map((e) => (
              <option key={e.id} value={e.id}>
                {e.name} ({e.local ? 'local' : 'cloud'})
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn ghost"
            disabled={!pick}
            onClick={() => {
              save([...order, pick]);
              setPick('');
            }}
          >
            Add model
          </button>
        </div>
      )}
      {error && (
        <p className="adm-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

// ─── One tenant's AI switches ────────────────────────────────────────────────

const SWITCHES: Array<{ key: keyof TenantAi; label: string; description: string }> = [
  {
    key: 'sentry_enabled',
    label: 'AI spam check',
    description:
      'Mail the spam filter cannot decide on is also read by a model, which looks for phishing, scams and impersonation.',
  },
  {
    key: 'compose_enabled',
    label: 'Writing assistant in webmail',
    description:
      'People can ask for a draft of a new message or a reply. Nothing is sent without them.',
  },
  {
    key: 'external_allowed',
    label: 'Allow cloud models',
    description: 'Content is only sent to cloud models when this is on. Off by default.',
  },
];

const SERVICE_LABEL: Record<AiService, string> = {
  sentry: 'Spam check',
  compose: 'Writing assistant',
};

export function TenantAiPanel({ slug, active }: { slug: string; active: boolean }) {
  const path = `/platform/tenants/${encodeURIComponent(slug)}/ai`;
  const [load, reload] = useLoad((signal) => api<TenantAiView>(path, { signal }), `ai:${slug}`);
  return (
    <section className="adm-panel">
      <h3>AI features</h3>
      <Loaded load={load}>
        {(view) => (
          <>
            <TenantAiForm
              key={JSON.stringify(view.settings)}
              path={path}
              settings={view.settings}
              editable={active}
              onSaved={reload}
            />
            <AiUsage usage={view.usage} />
          </>
        )}
      </Loaded>
    </section>
  );
}

function TenantAiForm({
  path,
  settings,
  editable,
  onSaved,
}: {
  path: string;
  settings: TenantAi;
  editable: boolean;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<TenantAi>(settings);
  const changed = SWITCHES.some(({ key }) => form[key] !== settings[key]);
  return (
    <>
      <ul className="adm-ai-switches">
        {SWITCHES.map(({ key, label, description }) => (
          <li key={key}>
            <label className="adm-check">
              <input
                type="checkbox"
                checked={form[key]}
                disabled={!editable}
                onChange={(e) => setForm({ ...form, [key]: e.target.checked })}
              />
              <span>
                <strong>{label}</strong>
                <span className="adm-muted adm-small">{description}</span>
              </span>
            </label>
          </li>
        ))}
      </ul>
      {editable && (
        <div className="adm-inline">
          <ActionButton
            disabled={!changed}
            run={async () => {
              await api(path, { method: 'POST', body: form });
              onSaved();
            }}
          >
            Save AI settings
          </ActionButton>
        </div>
      )}
    </>
  );
}

function AiUsage({ usage }: { usage: readonly AiUsageDay[] }) {
  const totals = (['sentry', 'compose'] as const)
    .map((service) => {
      const days = usage.filter((d) => d.service === service);
      return {
        service,
        requests: days.reduce((n, d) => n + d.requests, 0),
        failures: days.reduce((n, d) => n + d.failures, 0),
        tokens: days.reduce((n, d) => n + d.prompt_tokens + d.completion_tokens, 0),
      };
    })
    .filter((t) => t.requests > 0);
  return (
    <div className="adm-sub">
      <h4>Last 30 days</h4>
      {totals.length === 0 ? (
        <p className="adm-muted">No use in the last 30 days.</p>
      ) : (
        <table className="adm-table">
          <thead>
            <tr>
              <th>Feature</th>
              <th>Requests</th>
              <th>Failed</th>
              <th>Tokens</th>
            </tr>
          </thead>
          <tbody>
            {totals.map((t) => (
              <tr key={t.service}>
                <td>{SERVICE_LABEL[t.service]}</td>
                <td>{t.requests.toLocaleString()}</td>
                <td>{t.failures.toLocaleString()}</td>
                <td>{t.tokens.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
